import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutGrid, Trash2, GitCompare, Search, Clock,
  User, ChevronRight, Inbox
} from "lucide-react";
import { useParseHistory } from "../hooks/useParseHistory";

function HistoryItem({ entry, selected, onSelect, onDelete }) {
  const { result, filename, timestamp } = entry;
  const c = result.candidate || result;
  const name = c.full_name ||
    [c.first_name, c.last_name].filter(Boolean).join(" ") || "Unknown";
  const title = c.current_job_title || "";
  const employer = c.current_employer || "";
  const skills = Array.isArray(c.skill_set) ? c.skill_set : [];
  const date = new Date(timestamp);
  const dateStr = date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  const timeStr = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  return (
    <div
      style={{
        padding: "14px 16px",
        background: selected ? "rgba(200,241,53,0.06)" : "var(--ink-900)",
        border: `1px solid ${selected ? "rgba(200,241,53,0.3)" : "var(--slate-border)"}`,
        borderRadius: 10,
        cursor: "pointer",
        transition: "all 0.15s ease",
        position: "relative",
      }}
      onClick={() => onSelect(entry)}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        {/* Selection checkbox */}
        <div
          onClick={(e) => { e.stopPropagation(); onSelect(entry); }}
          style={{
            width: 16,
            height: 16,
            borderRadius: 4,
            border: `1px solid ${selected ? "var(--acid)" : "var(--slate-border)"}`,
            background: selected ? "var(--acid)" : "transparent",
            flexShrink: 0,
            marginTop: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.15s ease",
          }}
        >
          {selected && (
            <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
              <path d="M1 4L3.5 6.5L9 1" stroke="var(--ink-950)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>

        {/* Avatar */}
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 8,
            background: "var(--ink-700)",
            border: "1px solid var(--slate-border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <User size={16} color="var(--slate-text)" strokeWidth={1.5} />
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: "Syne, sans-serif",
              fontWeight: 600,
              fontSize: 14,
              color: "#E8EDF3",
              marginBottom: 3,
            }}
          >
            {name}
          </div>
          {(title || employer) && (
            <div style={{ fontSize: 12, color: "var(--slate-text)", marginBottom: 4 }}>
              {title}{employer ? ` @ ${employer}` : ""}
            </div>
          )}
          <div
            style={{
              fontSize: 10,
              color: "var(--slate-muted)",
              fontFamily: "JetBrains Mono, monospace",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {filename}
          </div>
        </div>

        {/* Date + actions */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Clock size={10} color="var(--slate-muted)" />
            <span
              style={{
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 10,
                color: "var(--slate-muted)",
              }}
            >
              {dateStr} {timeStr}
            </span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(entry.id); }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
              borderRadius: 4,
              color: "var(--slate-muted)",
              display: "flex",
              alignItems: "center",
            }}
            title="Delete"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {/* Skills preview */}
      {skills.length > 0 && (
        <div style={{ marginTop: 10, display: "flex", gap: 4, flexWrap: "wrap", paddingLeft: 66 }}>
          {skills.slice(0, 4).map((s, i) => (
            <span key={i} className="skill-pill" style={{ fontSize: 9 }}>{s}</span>
          ))}
          {skills.length > 4 && (
            <span
              style={{
                fontSize: 9,
                color: "var(--slate-muted)",
                fontFamily: "JetBrains Mono, monospace",
                alignSelf: "center",
              }}
            >
              +{skills.length - 4}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function HistoryPage() {
  const navigate = useNavigate();
  const { history, removeEntry, clearAll } = useParseHistory();
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");

  const toggleSelect = (entry) => {
    setSelected((prev) =>
      prev.find((e) => e.id === entry.id)
        ? prev.filter((e) => e.id !== entry.id)
        : [...prev, entry].slice(-2) // max 2 for compare
    );
  };

  const filtered = history.filter((e) => {
    const c = e.result.candidate || e.result;
    const name = c.full_name || [c.first_name, c.last_name].filter(Boolean).join(" ") || "";
    return (
      name.toLowerCase().includes(search.toLowerCase()) ||
      e.filename.toLowerCase().includes(search.toLowerCase())
    );
  });

  const handleCompare = () => {
    if (selected.length === 2) {
      const ids = selected.map((e) => e.id).join(",");
      navigate(`/compare?ids=${ids}`);
    }
  };

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "40px 32px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <LayoutGrid size={18} color="var(--acid)" />
            <h1
              style={{
                fontFamily: "Syne, sans-serif",
                fontWeight: 800,
                fontSize: 28,
                color: "#F0F4FA",
                margin: 0,
                letterSpacing: "-0.02em",
              }}
            >
              History
            </h1>
            <span
              style={{
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 11,
                color: "var(--slate-text)",
                background: "var(--ink-700)",
                border: "1px solid var(--slate-border)",
                padding: "2px 8px",
                borderRadius: 999,
              }}
            >
              {history.length}
            </span>
          </div>
          <p style={{ color: "var(--slate-text)", margin: 0, fontSize: 14 }}>
            Select two candidates to compare side-by-side
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {selected.length === 2 && (
            <button
              onClick={handleCompare}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                padding: "9px 16px",
                background: "var(--acid)",
                border: "none",
                borderRadius: 8,
                fontFamily: "Syne, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                color: "var(--ink-950)",
                cursor: "pointer",
              }}
            >
              <GitCompare size={14} strokeWidth={2.5} />
              Compare
              <ChevronRight size={13} />
            </button>
          )}
          {history.length > 0 && (
            <button
              onClick={clearAll}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 14px",
                background: "var(--ink-800)",
                border: "1px solid var(--slate-border)",
                borderRadius: 8,
                fontFamily: "DM Sans, sans-serif",
                fontSize: 13,
                color: "var(--slate-text)",
                cursor: "pointer",
              }}
            >
              <Trash2 size={13} />
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      {history.length > 0 && (
        <div
          style={{
            position: "relative",
            marginBottom: 20,
          }}
        >
          <Search
            size={14}
            color="var(--slate-muted)"
            style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or filename…"
            style={{
              width: "100%",
              padding: "10px 12px 10px 34px",
              background: "var(--ink-800)",
              border: "1px solid var(--slate-border)",
              borderRadius: 8,
              color: "#E8EDF3",
              fontSize: 13,
              fontFamily: "DM Sans, sans-serif",
              outline: "none",
            }}
          />
        </div>
      )}

      {/* Selection hint */}
      {selected.length > 0 && selected.length < 2 && (
        <div
          style={{
            padding: "8px 14px",
            background: "rgba(200,241,53,0.05)",
            border: "1px solid rgba(200,241,53,0.15)",
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 12,
            color: "var(--acid)",
            fontFamily: "JetBrains Mono, monospace",
          }}
        >
          Select one more candidate to compare →
        </div>
      )}

      {/* Empty state */}
      {history.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "80px 32px",
            color: "var(--slate-muted)",
          }}
        >
          <Inbox size={36} strokeWidth={1} style={{ margin: "0 auto 16px", display: "block" }} />
          <div
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: 16,
              color: "var(--slate-text)",
              marginBottom: 8,
            }}
          >
            No parsed CVs yet
          </div>
          <div style={{ fontSize: 13 }}>Upload and parse a CV to see it here</div>
        </div>
      )}

      {/* List */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {filtered.map((entry) => (
          <HistoryItem
            key={entry.id}
            entry={entry}
            selected={!!selected.find((e) => e.id === entry.id)}
            onSelect={toggleSelect}
            onDelete={removeEntry}
          />
        ))}
      </div>
    </div>
  );
}
